<template>
  <base-modal
    :ref="modal.key"
    :show="modal.open"
    :title="modalTitle"
    :subtitle="distribution.name"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    :maxWidth="maxWidth"
    :loadingContent="modal.loading"
  >
    <div v-if="!internal">
      <div v-for="(item, index) in distribution.details" :key="index">
        <v-row>
          <v-col cols="1" align-self="center">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  tile
                  large
                  color="error"
                  icon
                  @click="removeDetail(item.uuid)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("Remove") }}</span>
            </v-tooltip>
          </v-col>
          <v-col align-self="center">
            <v-row>
              <v-col align-self="center" cols="3">
                <v-combobox
                  v-model="item.externalUserEmail"
                  v-bind:label="$t('Email')"
                  :error-messages="externalUserEmailErrors(index)"
                  :items="distributedEmails"
                >
                  <template v-slot:append-outer>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          icon
                          small
                          v-on="on"
                          @click.prevent="searchEmails(item)"
                        >
                          <v-icon>mdi-magnify</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("SearchInDocuments") }}</span>
                    </v-tooltip>
                  </template>
                </v-combobox>
              </v-col>
              <v-col align-self="center">
                <v-switch
                  v-model="item.requiresValidation"
                  :label="$t('RequiresValidation')"
                  @change="requiresValidationChanged(item)"
                ></v-switch>
              </v-col>
              <v-col align-self="center" v-if="signatureEnabled">
                <v-switch
                  v-model="item.requiresSignature"
                  :label="$t('RequiresSignature')"
                  @change="requiresSignatureChanged(item)"
                ></v-switch>
              </v-col>
              <v-col align-self="center">
                <v-switch
                  v-model="item.singleVisualization"
                  :label="$t('SingleVisualization')"
                ></v-switch>
              </v-col>
              <v-col align-self="center">
                <v-switch
                  v-model="item.expires"
                  :label="$t('ExpiresIn')"
                ></v-switch>
              </v-col>
              <v-col v-if="item.expires" cols="1" align-self="center">
                <v-text-field
                  v-model="item.expirationDays"
                  :label="$t('Days')"
                  min="1"
                  step="1"
                  type="number"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col align-self="center" cols="3">
                <v-btn
                  color="blue darken-4"
                  outlined
                  width="100%"
                  @click="editInfoMessage(item)"
                >
                  {{ $t("Description") }}
                  <v-icon
                    v-if="item.infoMessage && !item.editInfoMessage"
                    right
                  >
                    mdi-pencil
                  </v-icon>
                  <v-icon
                    v-if="!item.infoMessage && !item.editInfoMessage"
                    right
                    >mdi-plus
                  </v-icon>
                  <v-icon v-if="item.editInfoMessage" right
                    >mdi-content-save</v-icon
                  >
                </v-btn>
              </v-col>
              <v-col align-self="center">
                <v-switch
                  v-model="item.allowDownload"
                  :label="$t('AllowDownload')"
                ></v-switch>
              </v-col>
              <v-col align-self="center">
                <v-switch
                  v-model="item.showAttachments"
                  :label="$t('IncludeAttachments')"
                ></v-switch>
              </v-col>
              <v-col align-self="center">
                <v-switch
                  v-if="item.showAttachments"
                  v-model="item.mergeAttachments"
                  :label="$t('MergeAttachments')"
                ></v-switch>
              </v-col>
              <v-col align-self="center" v-if="signatureEnabled"> </v-col>
              <v-col v-if="item.expires" cols="1" align-self="center"> </v-col>
            </v-row>
          </v-col>
          <v-col cols="1" align-self="center">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  tile
                  large
                  color="primary"
                  icon
                  @click="addDetail()"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("Add") }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row v-show="item.editInfoMessage">
          <v-col>
            <tiptap-vuetify
              v-model="item.infoMessage"
              :extensions="extensions"
            />
          </v-col>
        </v-row>
      </div>
    </div>
    <div v-if="internal">
      <v-card flat>
        <v-switch
          class="ma-3"
          style="position: absolute; top: 0; right: 0"
          v-model="distribution.notifyByEmail"
          :label="$t('NotifyByEmail')"
        ></v-switch>
        <v-card-subtitle>{{ $t("Users") }}</v-card-subtitle>
        <v-card-text>
          <v-row>
            <BaseDualList
              :source="availableUsers"
              :destination="destinationUsers"
              item-text="text"
              item-id="id"
              @onChangeList="onUserListChange"
            />
          </v-row>
        </v-card-text>
      </v-card>
    </div>
    <v-row>
      <v-col>
        <v-switch
          style="position: absolute"
          v-model="distribution.autoFinish"
          :label="$t('EndAutomatically')"
        ></v-switch>
      </v-col>
    </v-row>
    <v-dialog v-model="showFieldSearch" width="50%">
      <v-card>
        <v-card-text>
          <v-row v-for="(field, index) in selectedDocumentsEmails" :key="index">
            <v-col align-self="center">{{ field.title }}</v-col>
            <v-col align-self="center">{{ field.email }}</v-col>
            <v-col align-self="center" cols="1">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    tile
                    large
                    color="success"
                    icon
                    @click="selectEmail(field.email)"
                  >
                    <v-icon>mdi-check</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("Select") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </base-modal>
</template>
<script>
import DocumentService from "@/services/DocumentService";
import Distribution from "@/ecm/models/search/results/Distribution";
import DistributionDetail from "@/ecm/models/search/results/DistributionDetail";
import Utils from "@/ecm/common/Utils";
import { validationMixin } from "vuelidate";
import { requiredIf, email } from "vuelidate/lib/validators";
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from "tiptap-vuetify";

export default {
  components: {
    TiptapVuetify,
  },
  data() {
    return {
      availableUsers: [],
      destinationUsers: [],
      distributedEmails: [],
      selectedDocumentsEmails: [],
      selectedDocuments: [],
      signatureEnabled: false,
      showFieldSearch: false,
      modal: {
        title: this.$t("Distribution"),
        key: "distributionModal",
        open: false,
        loading: false,
      },
      internal: false,
      distribution: new Distribution(),
      currentItem: new DistributionDetail(),
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [
          Heading,
          {
            options: {
              levels: [1, 2, 3],
            },
          },
        ],
        Bold,
        HorizontalRule,
        Paragraph,
        HardBreak,
      ],
    };
  },
  mixins: [validationMixin],
  validations: {
    distribution: {
      details: {
        $each: {
          externalUserEmail: {
            required: requiredIf(function () {
              return !this.internal;
            }),
            email,
          },
        },
      },
    },
  },
  created() {
    this.checkSignatureEnabled();
  },
  computed: {
    modalInstance() {
      return this.$refs[this.modal.key];
    },
    modalTitle() {
      return this.internal
        ? this.$t("DistributeInternal")
        : this.$t("DistributeExternal");
    },
    maxWidth() {
      return this.internal ? "50%" : "80%";
    },
    selectedDocumentIds() {
      return this.selectedDocuments.map((d) => d.ObjectID);
    },
    defaultEmail() {
      return this.selectedDocumentsEmails.length > 0
        ? this.selectedDocumentsEmails[0].email
        : "";
    },
  },
  methods: {
    async getSelectedDocumentsEmails() {
      function validateEmail(email) {
        const re =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
      }
      var fieldsEmails = [
        ...new Set(
          this.selectedDocuments.flatMap((d) =>
            d.Fields.filter((f) => validateEmail(f.Value)).map((f) => {
              return { title: f.Title, email: f.Value };
            })
          )
        ),
      ];
      var contentEmails = await DocumentService.findEmailsInDocumentsContent(
        this.selectedDocumentIds
      );
      this.selectedDocumentsEmails = [
        ...fieldsEmails,
        ...contentEmails.map((email) => {
          return { title: this.$t("Fulltext"), email: email };
        }),
      ];
    },
    checkSignatureEnabled() {
      DocumentService.canSignExternal().then((result) => {
        this.signatureEnabled = result;
      });
    },
    externalUserEmailErrors(index) {
      const errors = [];
      if (!this.$v.distribution.details.$each[index].externalUserEmail.$dirty)
        return errors;
      !this.$v.distribution.details.$each[index].externalUserEmail.required &&
        errors.push(this.$t("RequiredField"));
      !this.$v.distribution.details.$each[index].externalUserEmail.email &&
        errors.push(this.$t("InvalidEmail"));
      return errors;
    },
    getAvailableUsers() {
      return DocumentService.getUsersForDistributionList(
        this.selectedDocumentIds
      ).then((data) => {
        this.availableUsers = data;
        this.destinationUsers = [];
      });
    },
    searchEmails(item) {
      this.currentItem = item;
      this.showFieldSearch = true;
    },
    selectEmail(email) {
      this.currentItem.externalUserEmail = email;
      this.showFieldSearch = false;
    },
    getDistributedEmails() {
      DocumentService.getDistributedEmails().then((data) => {
        this.distributedEmails = data;
      });
    },
    onUserListChange: function ({ destination, source }) {
      this.availableUsers = source;
      this.destinationUsers = destination;
      this.distribution.details = destination.map(
        (x) => new DistributionDetail(x.id)
      );
    },
    saveItem() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      if (!this.IsValid()) {
        return;
      }

      this.modalInstance.progress(true);

      DocumentService.distribute(this.distribution.toDto())
        .then((result) => {
          if (result) {
            if(this.internal)
            {
              Utils.toast.success(this.$t("DocumentsDistributedSuccessfully") + " (" +this.$t("Internal") +")");
            }
            else
            {
              Utils.toast.success(this.$t("DocumentsDistributedSuccessfully") + " (" +this.$t("External") +")");
            }
            this.hide();
            this.$emit("on-distributed");
          }
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.modalInstance.progress(false);
        });
    },
    IsValid() {
      if (this.distribution.details.length == 0) {
        Utils.toast.error(this.$t("ItemsSelectionError"));
        return false;
      }
      return true;
    },
    editInfoMessage(item) {
      item.editInfoMessage = !item.editInfoMessage;
    },
    async new(selectedDocuments, internal) {
      this.modal.loading = true;
      this.selectedDocuments = selectedDocuments;
      this.internal = internal;
      this.distribution = new Distribution(this.selectedDocumentIds);
      if (internal) {
        this.getAvailableUsers().then(() => {
          this.modal.loading = false;
        });
      } else {
        var canDistributeExternal = await this.validateDocumentDisplay();
        if (!canDistributeExternal) {
          return;
        }
        this.getDistributedEmails();
        this.getSelectedDocumentsEmails().then(() => {
          this.addDetail();
          this.modal.loading = false;
        });
      }
      this.show();
    },
    async validateDocumentDisplay() {
      var errorList = [];
      var canDisplayPromises = this.selectedDocumentIds.map(async function (
        objectID
      ) {
        return DocumentService.canDisplay(objectID)
          .then((result) => {
            if (!result) {
              errorList.push(objectID);
            }
          })
          .catch((err) => {
            Utils.toast.error(err);
          });
      });
      await Promise.all(canDisplayPromises);
      if (errorList.length > 0) {
        Utils.toast.error(
          `${this.$t("UnsupportedFileFormatException")} - ${this.$t(
            "Documents"
          )}: ${errorList.join()}`
        );
        return false;
      }
      return true;
    },
    addDetail() {
      var detail = new DistributionDetail();
      detail.externalUserEmail = this.defaultEmail;
      this.distribution.details.push(detail);
    },
    removeDetail(uuid) {
      this.distribution.details = this.distribution.details.filter(
        (item) => item.uuid !== uuid
      );
      if (this.distribution.details.length == 0) {
        this.addDetail();
      }
    },
    show() {
      this.modal.open = true;
    },
    hide() {
      this.modal.open = false;
    },
    requiresValidationChanged(item) {
      if (item.requiresValidation) {
        item.requiresSignature = false;
      }
    },
    requiresSignatureChanged(item) {
      if (item.requiresSignature) {
        item.requiresValidation = false;
      }
    },
  },
};
</script>
